import { useState } from "react";
import { useSession } from "@core/session/SessionProvider";
import { ApiError } from "@core/http";
import { tokens } from "@core/theme/tokens";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import {
  DialogHeader,
  EmptyResultsPanel,
  Field,
  inputStyle,
  Modal,
  PortalButton,
  RowActionsCell,
  RowIconAction,
  selectStyle,
  useToast,
} from "@portal/common";
import type { PortalRequest, RequestFilter, RequestStatus } from "./api";
import {
  assignRequestSchema,
  CLOSE_NOTE_REQUIRED_MESSAGE,
  closeRequestSchema,
} from "./requestSchemas";
import {
  useAssignableStaff,
  useRequestMutations,
  useRequests,
} from "./requestQueries";

// SPEC-214 §4/§5, ANNEX-009 §2, criterio 5: el tipo lo determina el origen,
// no se digita — acá solo se traduce el valor que ya trae el servidor.
const TYPE_LABEL: Record<PortalRequest["kind"], string> = {
  offer: "Oferta",
  part: "Repuesto",
};

// Prototipo (admin.jsx:539): tono por estado, palabra por palabra.
const STATUS_LABEL: Record<RequestStatus, string> = {
  new: "Nueva",
  assigned: "Asignada",
  contacted: "Contactado",
  closed: "Cerrada",
};
const STATUS_TONE: Record<RequestStatus, string> = {
  new: "gold-strong",
  assigned: "gold",
  contacted: "silver",
  closed: "neutral",
};

// portal.requests.filter-status (authority_ref PA-031-A): apertura por
// omisión en "nuevas" (SPEC-214 §4, RF-SOL-04, criterio 9). "" es "todas",
// no expuesto como estado del dominio.
const FILTER_CHIPS: Array<{ value: RequestStatus | ""; label: string }> = [
  { value: "new", label: "Nuevas" },
  { value: "assigned", label: "Asignadas" },
  { value: "contacted", label: "Contactado" },
  { value: "closed", label: "Cerradas" },
  { value: "", label: "Todas" },
];

// SPEC-214 §4: "antigüedad, no fecha absoluta" — mismo registro del
// prototipo ("Hace 4 min", "Hace 1 h", "Ayer"). No usa `Intl.RelativeTimeFormat`
// porque el prototipo fija un vocabulario propio en español, no genérico.
function formatAge(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Hace instantes";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Ayer";
  return `Hace ${days} d`;
}

// Fecha de calendario pura `format: date` (sin hora ni zona) — igual técnica
// que `formatOfferDate` en offers/OffersScreen.tsx: nunca `new Date(value)` +
// `toLocaleDateString`, corre un día hacia atrás según el huso del navegador.
function formatDMY(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const [, , month, day] = match;
  return `${day}/${month}`;
}

function itemLabel(row: PortalRequest): string {
  if (row.kind === "part") {
    const vehicle = row.item.vehicle;
    const vehicleText = vehicle
      ? [vehicle.model, vehicle.plate].filter(Boolean).join(" · ")
      : null;
    return [row.item.partDetail, vehicleText].filter(Boolean).join(" — ");
  }
  return row.item.offerTitle ?? "Oferta";
}

// El canónico (admin.jsx:548,551) usa `1.2fr 2fr 0.8fr 0.8fr 0.9fr` porque
// ahí Estado es la última columna, sin acciones a la derecha. Acá el brief
// agrega una sexta columna de acciones (fuera del prototipo, SPEC-214 §5) que
// no existe en `AdminSolicitudes`. Con las cinco columnas de datos en `fr` y
// una fila `assigned`/`contacted` que además muestra sus tres botones, la
// proporción `0.9fr` deja del ancho real menos margen para "Asignada" /
// "Contactado" que en el canónico (donde esa fr recibe todo el remanente) y
// la insignia se superpone con "Reasignar" (evidencia:
// portal-requests-assigned@1440x900.png). Fijar Estado a un ancho absoluto
// mantiene las cuatro columnas previas en las mismas proporciones relativas
// del canónico y garantiza el espacio de la insignia sin depender del ancho
// total disponible.
const COLS = "1.2fr 2fr 0.8fr 0.8fr 130px 230px";

// QA-MTZ-002 #12 (decisión humana 2026-09-23): la API valida transiciones.
// Una solicitud cerrada rechaza cualquier acción y marcar dos veces como
// contactada también se rechaza; los tres `onError` comparten el mapa.
const REQUEST_ERROR_MESSAGES: Partial<Record<string, string>> = {
  REQUEST_CLOSED: "La solicitud ya está cerrada.",
  REQUEST_ALREADY_CONTACTED: "La solicitud ya está marcada como contactada.",
};

// portal.requests.row-actions (authority_ref PA-031-A): asignar, marcar
// contactado y cerrar con nota opcional. QA-MTZ-002 #12 (decisión humana
// 2026-09-23): «Marcar contactado» solo se ofrece con estado `assigned` —
// una `new` nunca tiene responsable y siempre terminaba en `UNASSIGNED`.
// Ocultar un botón acá no es control de acceso (ADR-011): la autorización
// real la resuelve el servidor.
function RowActions({
  row,
  canEdit,
  onAssign,
  onContact,
  onClose,
  contactPending,
}: {
  row: PortalRequest;
  canEdit: boolean;
  onAssign: () => void;
  onContact: () => void;
  onClose: () => void;
  contactPending: boolean;
}) {
  if (!canEdit) return null;
  // Revisión en vivo 2026-09-22, punto #10 (barrido): tres acciones de texto
  // por fila, dos de ellas largas ("Marcar contactado", "Reasignar"), sobre
  // `.chip` crudo con el padding base 9px 15px — o sea que además no seguían
  // el tamaño único de botón de acción de fila del punto #6. Pasan a ícono +
  // tooltip: el grupo mide 3×40 + 2×6 = 132px pase lo que pase con el largo
  // de las etiquetas.
  return (
    <RowActionsCell>
      {/* SPEC-214 §5: la gestión concluye al cerrar. Sin `authority_ref` que
        autorice reabrir una solicitud cerrada reasignándola, se oculta junto
        con las otras dos acciones de estado (finding warn, intento 1). */}
      {row.status !== "closed" ? (
        <RowIconAction
          label={row.assignedTo ? "Reasignar" : "Asignar"}
          icon="invite"
          onClick={onAssign}
        />
      ) : null}
      {row.status === "assigned" ? (
        <RowIconAction
          label="Marcar contactado"
          icon="chat"
          onClick={onContact}
          disabled={contactPending}
        />
      ) : null}
      {row.status !== "closed" ? (
        <RowIconAction label="Cerrar" icon="check" onClick={onClose} />
      ) : null}
    </RowActionsCell>
  );
}

export function RequestsScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const canEdit = hasPermission("members", "canEdit");
  const [status, setStatus] = useState<RequestStatus | "">("new");
  const filter: RequestFilter = { status };
  const requests = useRequests(filter);
  const staff = useAssignableStaff();
  const mutations = useRequestMutations();

  const [assigning, setAssigning] = useState<PortalRequest | null>(null);
  const [assignValue, setAssignValue] = useState("");
  const [assignError, setAssignError] = useState<string | undefined>(undefined);
  const [closing, setClosing] = useState<PortalRequest | null>(null);
  const [closeNote, setCloseNote] = useState("");
  const [closeNoteError, setCloseNoteError] = useState<string | undefined>(
    undefined,
  );

  const pages = requests.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.items);
  const lastPage = pages.at(-1);

  const openAssign = (row: PortalRequest) => {
    setAssignValue(row.assignedTo?.id ?? "");
    setAssignError(undefined);
    setAssigning(row);
  };

  const submitAssign = () => {
    const parsed = assignRequestSchema.safeParse({ assignedTo: assignValue });
    if (!parsed.success) {
      // Único campo del formulario: cada issue (solo puede haber uno) va
      // bajo Responsable.
      for (const issue of parsed.error.issues) setAssignError(issue.message);
      return;
    }
    if (!assigning) return;
    mutations.assign.mutate(
      { id: assigning.id, assignedTo: parsed.data.assignedTo },
      {
        onSuccess: () => {
          toast.show("Solicitud asignada");
          setAssigning(null);
        },
        // PLAN-009 §3: el slot de campo (`assignError`) queda solo para el
        // issue de zod de arriba — un error de servicio va siempre al
        // toast.
        onError: (error) => reportError(toast, error, REQUEST_ERROR_MESSAGES),
      },
    );
  };

  const markContacted = (row: PortalRequest) => {
    mutations.contact.mutate(row.id, {
      onSuccess: () => toast.show("Solicitud marcada como contactada"),
      onError: (error) => {
        // SPEC-214 §8: mensaje literal `UNASSIGNED`. Con la acción limitada
        // a `assigned` ya no se dispara desde una fila nueva, pero sigue
        // alcanzable si el responsable se quitó en otra sesión y la lista
        // aún no se refrescó.
        if (error instanceof ApiError && error.code === "UNASSIGNED") {
          toast.show(
            "Asigna un responsable antes de marcar como contactado",
            "neutral",
          );
          markErrorHandled(error);
          return;
        }
        reportError(toast, error, REQUEST_ERROR_MESSAGES);
      },
    });
  };

  const openClose = (row: PortalRequest) => {
    setCloseNote("");
    setCloseNoteError(undefined);
    setClosing(row);
  };

  const submitClose = () => {
    if (!closing) return;
    const parsed = closeRequestSchema.safeParse({ note: closeNote });
    if (!parsed.success) {
      // Único campo del formulario: el issue va bajo «Nota».
      for (const issue of parsed.error.issues) setCloseNoteError(issue.message);
      return;
    }
    setCloseNoteError(undefined);
    mutations.close.mutate(
      { id: closing.id, note: parsed.data.note },
      {
        onSuccess: () => {
          toast.show("Solicitud cerrada");
          setClosing(null);
        },
        // Si la API rechaza la nota (INVALID_REQUEST en `note`), el toast
        // dice lo mismo que el campo; el resto de los códigos, su mensaje.
        onError: (error) =>
          reportError(toast, error, {
            ...REQUEST_ERROR_MESSAGES,
            INVALID_REQUEST: CLOSE_NOTE_REQUIRED_MESSAGE,
          }),
      },
    );
  };

  return (
    <div>
      {/* Título de página genérico, nivel que replican todas las pantallas
        de nivel superior del portal con el mismo literal (MembersScreen.tsx,
        CircleManagementScreen.tsx, CatalogsScreen.tsx, HomeContentScreen.tsx,
        WelcomeContentScreen.tsx). Rótulo del ítem de navegación (admin.jsx:16,
        `NAV`) + descripción de pestaña (admin.jsx:3842). No confundir con el
        título interno de la tarjeta (portal.requests.title, más abajo). */}
      <header style={{ marginBottom: 0 }}>
        <h1 style={{ margin: 0, font: `300 34px ${tokens.font.display}` }}>
          Solicitudes
        </h1>
        <p
          style={{
            color: tokens.text.tertiary,
            margin: "2px 0 26px",
            fontSize: 13,
          }}
        >
          Bandeja de lo que los miembros piden desde la app.
        </p>
      </header>

      {/* portal.requests.filter-status */}
      <div
        role="group"
        aria-label="Filtrar por estado"
        style={{ display: "flex", gap: 8, marginBottom: 16 }}
      >
        {FILTER_CHIPS.map((chip) => (
          <button
            key={chip.value || "all"}
            type="button"
            className={`chip${status === chip.value ? " on" : ""}`}
            aria-pressed={status === chip.value}
            onClick={() => setStatus(chip.value)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div
        style={{
          border: `1px solid ${tokens.surface.line}`,
          borderRadius: 18,
          background: tokens.surface.bg2,
        }}
      >
        {/* portal.requests.title / .subtitle (authority_ref PA-031-B):
          ANNEX-009 retiró "beneficio" del título interno de la tarjeta
          (admin.jsx:543/545, `AdminSolicitudes`); el aviso por correo queda
          fuera de alcance (SPEC-214 §2). `h2`, no `h3`: mismo ajuste de
          heading-order que welcome-content, porque el `h1` de página es
          nuevo acá y antes no había ninguno previo que ordenar. */}
        <div style={{ padding: "22px 24px 18px" }}>
          <h2 className="display" style={{ fontSize: 22, margin: "0 0 4px" }}>
            Solicitudes de ofertas y repuestos
          </h2>
          <div
            style={{
              font: `400 12px ${tokens.font.ui}`,
              color: tokens.text.tertiary,
            }}
          >
            Cada «Quiero esta oferta» del miembro y cada solicitud de repuesto
            llegan aquí. El contacto posterior es manual.
          </div>
        </div>
        {requests.isLoading ? (
          <div style={{ padding: 24, color: tokens.text.tertiary }}>
            Cargando…
          </div>
        ) : rows.length === 0 ? (
          <EmptyResultsPanel
            icon="chat"
            title="Ninguna solicitud en este estado"
            description="Cuando un miembro pida una oferta o un repuesto, aparece acá."
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 1080 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  gap: 14,
                  padding: "18px 24px 11px",
                  borderBottom: `1px solid ${tokens.surface.line}`,
                }}
              >
                {["Miembro", "Solicitud", "Tipo", "Recibida", "Estado"].map(
                  (h) => (
                    <span key={h} className="label">
                      {h}
                    </span>
                  ),
                )}
                <span className="label" style={{ textAlign: "right" }}>
                  Acciones
                </span>
              </div>
              {rows.map((row) => {
                // portal.requests.row-part-highlight (authority_ref
                // PA-031-A): destaque visual sin reordenamiento automático.
                // `tokens.gold` está reservado a recompensas y logros
                // (tokens.ts): el destaque usa `silver`/`surface`, no gold.
                const highlighted = row.kind === "part";
                const expiredAt =
                  row.kind === "offer" && row.item.offerExpired
                    ? formatDMY(row.item.offerValidUntil)
                    : null;
                return (
                  <div
                    key={row.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: COLS,
                      gap: 14,
                      alignItems: "center",
                      padding: "14px 24px",
                      borderBottom: `1px solid ${tokens.surface.line}`,
                      ...(highlighted
                        ? {
                            background: tokens.surface.surface2,
                            borderLeft: `3px solid ${tokens.silver.flat}`,
                          }
                        : {}),
                    }}
                  >
                    <span
                      style={{
                        font: `600 13px ${tokens.font.ui}`,
                        color: tokens.text.primary,
                      }}
                    >
                      {row.member.name}
                    </span>
                    <div>
                      <div
                        style={{
                          font: `400 13px ${tokens.font.ui}`,
                          color: tokens.text.secondary,
                        }}
                      >
                        {itemLabel(row)}
                      </div>
                      {/* portal.requests.row-offer-expired (authority_ref
                        PA-031-A): la solicitud sigue abierta, se señala nomás. */}
                      {expiredAt ? (
                        <div
                          role="status"
                          style={{
                            font: `500 11px ${tokens.font.ui}`,
                            color: tokens.danger.base,
                            marginTop: 2,
                          }}
                        >
                          La oferta asociada venció el {expiredAt}
                        </div>
                      ) : null}
                    </div>
                    {/* portal.requests.row-type-part (authority_ref PA-031-A) */}
                    <span
                      style={{
                        font: `400 12px ${tokens.font.ui}`,
                        color: tokens.text.tertiary,
                      }}
                    >
                      {TYPE_LABEL[row.kind]}
                    </span>
                    <span
                      style={{
                        font: `400 12px ${tokens.font.ui}`,
                        color: tokens.text.tertiary,
                      }}
                    >
                      {formatAge(row.createdAt)}
                    </span>
                    <span>
                      <span className={`stat stat-${STATUS_TONE[row.status]}`}>
                        {STATUS_LABEL[row.status]}
                      </span>
                    </span>
                    <RowActions
                      row={row}
                      canEdit={canEdit}
                      onAssign={() => openAssign(row)}
                      onContact={() => markContacted(row)}
                      onClose={() => openClose(row)}
                      contactPending={mutations.contact.isPending}
                    />
                  </div>
                );
              })}
              {lastPage?.meta.next ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "14px 24px",
                  }}
                >
                  <button
                    type="button"
                    className="btn"
                    disabled={requests.isFetchingNextPage}
                    onClick={() => requests.fetchNextPage()}
                  >
                    {requests.isFetchingNextPage ? "Cargando…" : "Cargar más"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {assigning ? (
        <Modal
          label={
            assigning.assignedTo ? "Reasignar solicitud" : "Asignar solicitud"
          }
          width={440}
          onClose={() => setAssigning(null)}
          // Revisión en vivo 2026-09-22, punto #15: este diálogo era la cuarta
          // estructura de modal del portal (`<h2>` crudo, sin divisores,
          // botones `.btn`/`.btn-primary`). Ahora: encabezado y pie fijos del
          // `Modal`, título con la tipografía del sistema y `PortalButton`.
          // Punto #21 (misma revisión): encabezado común de diálogos.
          header={
            <DialogHeader
              eyebrow="Solicitudes"
              title={
                assigning.assignedTo
                  ? "Reasignar solicitud"
                  : "Asignar responsable"
              }
            />
          }
          footer={
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <PortalButton
                type="button"
                tone="secondary"
                size="small"
                onClick={() => setAssigning(null)}
              >
                Cancelar
              </PortalButton>
              <PortalButton
                type="button"
                size="small"
                onClick={submitAssign}
                disabled={mutations.assign.isPending}
              >
                {mutations.assign.isPending ? "Asignando…" : "Asignar"}
              </PortalButton>
            </div>
          }
        >
          <Field label="Responsable" required error={assignError}>
            <select
              value={assignValue}
              onChange={(event) => setAssignValue(event.target.value)}
              style={selectStyle}
            >
              <option value="">Selecciona un responsable</option>
              {staff.isLoading ? (
                <option value="" disabled>
                  Cargando…
                </option>
              ) : (
                staff.data?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))
              )}
            </select>
          </Field>
        </Modal>
      ) : null}

      {closing ? (
        <Modal
          label="Cerrar solicitud"
          width={440}
          onClose={() => setClosing(null)}
          // Misma convergencia que "Asignar responsable" (punto #15): el
          // diálogo hermano compartía su estructura suelta.
          // Punto #21 (misma revisión): encabezado común de diálogos.
          header={
            <DialogHeader eyebrow="Solicitudes" title="¿Cerrar la solicitud?" />
          }
          footer={
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <PortalButton
                type="button"
                tone="secondary"
                size="small"
                onClick={() => setClosing(null)}
              >
                Cancelar
              </PortalButton>
              <PortalButton
                type="button"
                size="small"
                onClick={submitClose}
                disabled={mutations.close.isPending}
              >
                {mutations.close.isPending ? "Cerrando…" : "Cerrar"}
              </PortalButton>
            </div>
          }
        >
          <p style={{ color: tokens.text.secondary, margin: "0 0 14px" }}>
            La gestión concluye, con o sin concreción. Deja una nota con el
            desenlace.
          </p>
          <Field label="Nota" required error={closeNoteError}>
            <textarea
              value={closeNote}
              onChange={(event) => {
                setCloseNote(event.target.value);
                setCloseNoteError(undefined);
              }}
              style={{ ...inputStyle, minHeight: 84, resize: "vertical" }}
            />
          </Field>
        </Modal>
      ) : null}
    </div>
  );
}
